/**
 * Scene List Editor
 * Sprint 19: Custom Template Creation
 * 
 * Sortable list of scenes with drag-drop reordering and add/remove/duplicate capabilities
 */

import React, { useState } from 'react';
import { List, Button, Popconfirm, Tag, Space, Tooltip } from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  CopyOutlined,
  ClockCircleOutlined,
  HolderOutlined
} from '@ant-design/icons';
import { TemplateScene } from '../../../../types/template';
import './SceneListEditor.scss';

interface SceneListEditorProps {
  scenes: TemplateScene[];
  activeSceneIndex: number;
  onSelectScene: (index: number) => void;
  onAddScene: () => void;
  onRemoveScene: (index: number) => void;
  onDuplicateScene: (index: number) => void;
  onReorderScene: (fromIndex: number, toIndex: number) => void;
}

export const SceneListEditor: React.FC<SceneListEditorProps> = ({
  scenes,
  activeSceneIndex,
  onSelectScene,
  onAddScene,
  onRemoveScene,
  onDuplicateScene,
  onReorderScene
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const getTotalDuration = () => {
    return scenes.reduce((total, scene) => total + scene.suggestedDuration, 0);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const getSceneTagColor = (tags?: string[]) => {
    if (!tags || tags.length === 0) return 'default';
    const tag = tags[0];
    const colors: Record<string, string> = {
      'intro': 'green',
      'content': 'blue',
      'demo': 'purple',
      'cta': 'orange',
      'outro': 'cyan',
      'transition': 'magenta'
    };
    return colors[tag] || 'default';
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== toIndex) {
      onReorderScene(draggedIndex, toIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="scene-list-editor">
      <div className="scene-list-header">
        <h4>Scenes ({scenes.length})</h4>
        <Space>
          <Tag icon={<ClockCircleOutlined />}>
            Total: {formatDuration(getTotalDuration())}
          </Tag>
          <Tooltip title="Add Scene">
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={onAddScene}
            />
          </Tooltip>
        </Space>
      </div>

      <List
        className="scene-list"
        dataSource={scenes}
        renderItem={(scene, index) => (
          <List.Item
            className={`scene-item ${activeSceneIndex === index ? 'active' : ''} ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
            onClick={() => onSelectScene(index)}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <div className="scene-content">
              <div className="drag-handle" onMouseDown={(e) => e.stopPropagation()}>
                <HolderOutlined />
              </div>
              <div className="scene-number">
                <span>{index + 1}</span>
              </div>
              <div className="scene-info">
                <div className="scene-title">{scene.title}</div>
                <div className="scene-meta">
                  <Tag color={getSceneTagColor(scene.tags)}>
                    {scene.tags?.[0] || 'Scene'}
                  </Tag>
                  <span className="scene-duration">
                    {formatDuration(scene.suggestedDuration)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="scene-actions" onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Duplicate">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => onDuplicateScene(index)}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this scene?"
                description="This action cannot be undone."
                onConfirm={() => onRemoveScene(index)}
                okText="Delete"
                cancelText="Cancel"
                placement="left"
              >
                <Tooltip title="Delete">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={scenes.length <= 1}
                  />
                </Tooltip>
              </Popconfirm>
            </div>
          </List.Item>
        )}
      />

      {scenes.length === 0 && (
        <div className="empty-scenes">
          <p>No scenes yet. Add your first scene to get started.</p>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAddScene}>
            Add First Scene
          </Button>
        </div>
      )}
    </div>
  );
};

export default SceneListEditor;
